// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface LoginLog {
  id: string;
  user_id: string;
  email: string;
  name: string;
  login_time: string;
  ip_address?: string;
  user_agent?: string;
}

interface PurchaseLog {
  id: string;
  user_id: string;
  email: string;
  name: string;
  membership_name: string;
  amount: number;
  status: string;
  created_at: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
}

const LogsPage: React.FC = () => {
  const [loginLogs, setLoginLogs] = useState<LoginLog[]>([]);
  const [purchaseLogs, setPurchaseLogs] = useState<PurchaseLog[]>([]);
  const [loginSearch, setLoginSearch] = useState('');
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [loadingLogins, setLoadingLogins] = useState(false);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  const fetchLoginLogs = async () => {
    setLoadingLogins(true);
    try {
      // Get profiles with created_at as proxy for login time
      let query = supabase
        .from('profiles')
        .select('id, user_id, email, name, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (loginSearch) {
        query = query.or(`email.ilike.%${loginSearch}%,name.ilike.%${loginSearch}%`);
      }
      
      const { data: profileData, error: profileError } = await query;
      
      if (profileError) throw profileError;
      
      const logs: LoginLog[] = (profileData || []).map(profile => ({
        id: profile.id,
        user_id: profile.user_id || profile.id,
        email: profile.email || 'N/A',
        name: profile.name || 'N/A',
        login_time: profile.created_at || '',
      }));
      
      setLoginLogs(logs);
    } catch (error) {
      console.error('Error fetching login logs:', error);
      toast.error('Failed to fetch login logs');
    } finally {
      setLoadingLogins(false);
    }
  };

  const fetchPurchaseLogs = async () => {
    setLoadingPurchases(true);
    try {
      // Query user_memberships without foreign key relationships
      const { data: membershipData, error: membershipError } = await supabase
        .from('user_memberships')
        .select('id, user_id, membership_id, status, created_at, start_date, end_date')
        .order('created_at', { ascending: false })
        .limit(100);

      if (membershipError) {
        console.error('Membership query error:', membershipError);
        throw membershipError;
      }

      if (!membershipData || membershipData.length === 0) {
        setPurchaseLogs([]);
        return;
      }

      // Get unique user_ids and membership_ids
      const userIds = [...new Set(membershipData.map(m => m.user_id))];
      const membershipIds = [...new Set(membershipData.map(m => m.membership_id))];

      // Fetch profiles for these users
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_id, email, name')
        .in('user_id', userIds);

      // Fetch membership details
      const { data: membershipsInfo } = await supabase
        .from('memberships')
        .select('id, name, price_cents')
        .in('id', membershipIds);

      // Fetch subscription data for Stripe info (only columns that exist)
      const { data: subscriptionData } = await supabase
        .from('subscriptions')
        .select('user_id, stripe_customer_id, status, amount, plan_id')
        .in('user_id', userIds);

      // Create lookup maps
      const profileMap = new Map((profileData || []).map(p => [p.user_id, p]));
      const membershipMap = new Map((membershipsInfo || []).map(m => [m.id, m]));
      const stripeMap = new Map((subscriptionData || []).map(s => [s.user_id, s]));

      // Combine data
      let logs: PurchaseLog[] = membershipData.map((membership) => {
        const profile = profileMap.get(membership.user_id);
        const membershipInfo = membershipMap.get(membership.membership_id);
        const stripeData = stripeMap.get(membership.user_id);

        return {
          id: membership.id,
          user_id: membership.user_id,
          email: profile?.email || 'N/A',
          name: profile?.name || 'N/A',
          membership_name: membershipInfo?.name || 'N/A',
          amount: membershipInfo?.price_cents ? membershipInfo.price_cents / 100 : (stripeData?.amount ? stripeData.amount / 100 : 0),
          status: membership.status || 'unknown',
          created_at: membership.created_at,
          stripe_subscription_id: stripeData?.plan_id || 'N/A',
          stripe_customer_id: stripeData?.stripe_customer_id || 'N/A',
        };
      });

      // Filter by search if provided
      if (purchaseSearch) {
        const searchLower = purchaseSearch.toLowerCase();
        logs = logs.filter(log => 
          log.email.toLowerCase().includes(searchLower) ||
          log.name.toLowerCase().includes(searchLower) ||
          log.stripe_customer_id?.toLowerCase().includes(searchLower)
        );
      }

      setPurchaseLogs(logs);
    } catch (error) {
      console.error('Error fetching purchase logs:', error);
      toast.error('Failed to fetch purchase logs');
    } finally {
      setLoadingPurchases(false);
    }
  };

  useEffect(() => {
    fetchLoginLogs();
    fetchPurchaseLogs();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">System Logs</h1>
        <p className="text-muted-foreground mt-1">
          Track user logins and purchase activity
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activity Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="logins" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="logins">Logins</TabsTrigger>
              <TabsTrigger value="purchases">Purchases</TabsTrigger>
            </TabsList>

            <TabsContent value="logins" className="mt-6">
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email or name..."
                      value={loginSearch}
                      onChange={(e) => setLoginSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button
                    onClick={fetchLoginLogs}
                    disabled={loadingLogins}
                    variant="outline"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingLogins ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                <div className="rounded-md border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Last Login</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">User ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {loadingLogins ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                              Loading...
                            </td>
                          </tr>
                        ) : loginLogs.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                              No login logs found
                            </td>
                          </tr>
                        ) : (
                          loginLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-muted/50">
                              <td className="px-4 py-3 text-sm">{log.email}</td>
                              <td className="px-4 py-3 text-sm">{log.name}</td>
                              <td className="px-4 py-3 text-sm">
                                {log.login_time ? format(new Date(log.login_time), 'MMM dd, yyyy HH:mm') : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-xs">{log.user_id}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="purchases" className="mt-6">
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email, name, or Stripe ID..."
                      value={purchaseSearch}
                      onChange={(e) => setPurchaseSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button
                    onClick={fetchPurchaseLogs}
                    disabled={loadingPurchases}
                    variant="outline"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingPurchases ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                <div className="rounded-md border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Membership</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Stripe Sub ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {loadingPurchases ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                              Loading...
                            </td>
                          </tr>
                        ) : purchaseLogs.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                              No purchase logs found
                            </td>
                          </tr>
                        ) : (
                          purchaseLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-muted/50">
                              <td className="px-4 py-3 text-sm">{log.email}</td>
                              <td className="px-4 py-3 text-sm">{log.name}</td>
                              <td className="px-4 py-3 text-sm">{log.membership_name}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  log.status === 'active' ? 'bg-green-100 text-green-800' :
                                  log.status === 'canceled' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {log.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm')}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono text-xs">
                                {log.stripe_subscription_id || 'N/A'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default LogsPage;
